import React, { useState } from "react";
import Pagination from "../../tables/pagination";
import "./price-list-table.css";
import UpdateDisModal from "../../modals/discounts-modals/update-discount-modal";
import AddButton from "../../buttons/button";
import { useHotkeys } from "../../helper-components/use-hotkeys";

const PriceListItemTable = ({
  paginationProps,
  priceListData,
  setReload,
  reload,
}) => {
  // each entry: { item_id, item_batch_number }
  const [checkedId, setCheckedId] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Helper: check if this (id, batch) pair is selected
  const isRowChecked = (id, batch) =>
    checkedId.some(
      (row) =>
        row.item_id === id && row.item_batch_number === batch
    );

  function checkboxChecker(e, payload, all) {
    if (all === "all") {
      const items = payload || [];

      // Build all row pairs from itemslist
      const allPairs = items.map((item) => ({
        item_id: item.item_id,
        item_batch_number: item.item_batch_number,
      }));

      const allSelected =
        allPairs.length > 0 &&
        allPairs.every((pair) =>
          isRowChecked(pair.item_id, pair.item_batch_number)
        );

      if (allSelected) {
        // uncheck all
        setCheckedId([]);
      } else {
        // select all
        setCheckedId(allPairs);
      }
    } else {
      // Single row toggle: payload = rowData
      const row = payload;
      const id = row.item_id;
      const batch = row.item_batch_number;

      const exists = isRowChecked(id, batch);

      if (exists) {
        // remove that pair
        setCheckedId((prev) =>
          prev.filter(
            (item) =>
              !(
                item.item_id === id &&
                item.item_batch_number === batch
              )
          )
        );
      } else {
        // add that pair
        setCheckedId((prev) => [
          ...prev,
          { item_id: id, item_batch_number: batch },
        ]);
      }
    }
  }

  useHotkeys("escape", () => {
    setIsModalOpen(false);
  });

  const itemsList = priceListData?.itemslist || [];

  // Header "Select All" checkbox state
  const isAllChecked =
    itemsList.length > 0 &&
    itemsList.every((item) =>
      isRowChecked(item.item_id, item.item_batch_number)
    );

  return (
    <>
      <UpdateDisModal
        checkedId={checkedId} // now an array of { item_id, item_batch_number }
        setReload={setReload}
        reload={reload}
        priceListData={priceListData}
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
      />

      <div className="px-5 xl:px-3 mt-3">
        {checkedId?.length > 0 && (
          <AddButton
            onclkFn={() => setIsModalOpen(true)}
            prefixIcon={false}
            text="Update Items"
            type="button"
          />
        )}
      </div>

      <div className="flex flex-col justify-between h-full">
        <div className="price-tab-container">
          <table className="price-my-tab h-full">
            <thead>
              <tr>
                <th
                  className={`${"price-tab-header"} whitespace-nowrap fs_12 fw_700 text-475467 bg-F2F4F5`}
                >
                  <input
                    checked={isAllChecked}
                    onChange={(e) =>
                      checkboxChecker(
                        e,
                        itemsList,
                        "all"
                      )
                    }
                    style={{ transform: "scale(1.1)" }}
                    type="checkbox"
                    id="myCheckbox"
                    className="custom-checkbox form-check-input"
                  />
                </th>
                {tableHeading.map((heading, index) => (
                  <th
                    key={index}
                    className={`${"price-tab-header"} whitespace-nowrap fs_12 fw_700 text-475467 bg-F2F4F5`}
                  >
                    {heading?.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="h-full">
              {itemsList.length > 0 ? (
                <>
                  {itemsList.map((rowData, rowIndex) => (
                    <tr key={rowIndex}>
                      <td className="text-sm tab-cell fw_400 text-secondary">
                        <input
                          checked={isRowChecked(
                            rowData?.item_id,
                            rowData?.item_batch_number
                          )}
                          id={rowData?.item_id}
                          onChange={(e) =>
                            checkboxChecker(e, rowData)
                          }
                          style={{ transform: "scale(1.2)" }}
                          type="checkbox"
                          className="custom-checkbox form-check-input"
                        />
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_code}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_name ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_batch_number ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.default_base_price_list ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_mrp_price ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_base_price ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.default_factor}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_batch_purchase_rate_before_tax ??
                          "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.margin_factor ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_batch_final_purchase_rate ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_selling_price}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.discount_percentage ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.margin_percentage ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.is_manual ? "Yes" : "No"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_inventory_uom ?? "--"}
                      </td>
                      <td className="price-tab-cell text-sm fw_400 text-secondary whitespace-nowrap">
                        {rowData?.item_pricing_uom ?? "--"}
                      </td>
                    </tr>
                  ))}
                </>
              ) : (
                <tr
                  style={{
                    height: "20rem",
                  }}
                >
                  <td
                    colSpan={tableHeading.length + 1}
                    className="text-center text-primary"
                  >
                    No Items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination paginationProps={paginationProps} />
      </div>
    </>
  );
};

export default PriceListItemTable;

const tableHeading = [
  {
    label: "Item Code",
  },
  {
    label: "Product Name",
  },
  {
    label: "Batch Number",
  },
  {
    label: "Base Price List",
  },
  {
    label: "MRP",
  },
  {
    label: "Base Price",
  },
  {
    label: "% related to base pice",
  },
  {
    label: "Purchase Rate(excl tax)",
  },
  {
    label: "% related to Purchase Price",
  },
  {
    label: "Purchase Rate(inc tax)",
  },
  {
    label: "Selling Price",
  },
  {
    label: "Discount %",
  },
  {
    label: "Margin %",
  },
  {
    label: "Is Manual",
  },
  {
    label: "Inventory UOM",
  },
  {
    label: "Pricing UOM",
  },
];
